/**
 * `/missions` as a `SurfaceController`: the model in `missions-model.ts`, the
 * screens in `missions-screens.ts`, and the small amount of glue that turns a
 * command into a new model and a new mission.
 *
 * The mission itself is never stored — it is regenerated from the model's
 * catalog id, seed and side every time a screen is built, exactly as `/fly`
 * regenerates it from the URL. That is what keeps the briefing, the assignment
 * sheet, the roster and the sortie that is finally flown provably the same
 * mission.
 */

import { bestMissions, type MissionSpec } from "@red-opus/engine";
import type { SettingsStore } from "../audio/settings.ts";
import { browserBestMissionsStore, loadBestMissions, type BestMissionsStore } from "../missions/best-missions-store.ts";
import { missionFor } from "../mission/mission-launch.ts";
import { applyRealismCommand, currentRealism, saveRealism, type RealismPanel } from "../mission/realism-settings.ts";
import {
  initialMissions,
  missionsCommand,
  missionsLaunch,
  paramFieldOf,
  parameterDialog,
  type MissionsModel,
} from "./model/missions-model.ts";
import type { MenuAction } from "./model/menu-model.ts";
import { gridSlotById, MISSION_GRID_BACKGROUND } from "./model/mission-grid.ts";
import { PORTRAIT_SHEET } from "./model/historic-portrait.ts";
import {
  flightAssignmentsScreen,
  MISSION_BACKDROPS,
  missionAssignmentScreen,
  missionBriefingScreen,
  missionGridScreen,
} from "./model/missions-screens.ts";
import { bestMissionsScreen } from "./model/best-missions-screen.ts";
import type { ActionSpec } from "./model/panel-parts.ts";
import { realismScreen } from "./model/realism-screen.ts";
import type { ShellDialog, ShellScreen } from "./model/shell-screen.ts";
import { ORNAMENT_SHEET } from "./render/board-chrome.ts";
import type { FontId } from "./render/draw-ops.ts";
import { PANEL_SHEET } from "./render/panel-chrome.ts";
import type { SurfaceController } from "./screen-runtime.ts";
import type { FontAtlas } from "./render/text-metrics.ts";

const command = (name: string): MenuAction => ({ kind: "command", command: name });

/**
 * `PS.EXE` 257487's own run for this screen — the title and then `CANCEL`,
 * `FLIGHT ASSIGNMENTS`, `BEGIN MISSION` — which is what
 * `docs/game/ui-reference/mission-assignment.png` draws. The `START POSITION`
 * button that used to sit second is gone: four labels in FONT1 come to 282px
 * of ink and the frame cannot hold them at any padding the button chrome
 * allows, and the choice is reachable off the sheet's own `START` value.
 */
const ASSIGNMENT_ACTIONS: readonly ActionSpec[] = [
  { id: "cancel", label: "CANCEL", action: command("briefing") },
  { id: "flight", label: "FLIGHT ASSIGNMENTS", action: command("roster") },
  { id: "begin", label: "BEGIN MISSION", action: command("begin") },
];

const ROSTER_ACTIONS: readonly ActionSpec[] = [
  { id: "assignment", label: "MISSION ASSIGNMENT", action: command("assignment") },
  { id: "realism", label: "REALISM PANEL", action: command("realism") },
  { id: "begin", label: "BEGIN MISSION", action: command("begin") },
];

class MissionsSurface implements SurfaceController {
  readonly assets = {
    images: [MISSION_GRID_BACKGROUND, ...MISSION_BACKDROPS],
    // `PORTRAIT_SHEET` is only ever drawn on a historic encounter's briefing,
    // but the asset set is declared per surface rather than per screen, so it
    // is preloaded with the chrome.
    sprites: [ORNAMENT_SHEET, PANEL_SHEET, PORTRAIT_SHEET],
  };

  private model: MissionsModel;
  /** What `RESTORE` on the Realism Panel goes back to: the settings this visit opened with. */
  private readonly opening: RealismPanel;
  private readonly bestMissionsStore: BestMissionsStore | undefined;

  constructor(private readonly store: SettingsStore | undefined) {
    const panel = currentRealism(store);
    this.opening = panel;
    this.model = initialMissions(panel);
    this.bestMissionsStore = browserBestMissionsStore();
  }

  state(): MissionsModel {
    return this.model;
  }

  private spec(): MissionSpec | undefined {
    const launch = missionsLaunch(this.model);
    return launch ? missionFor(launch, this.model.realism) : undefined;
  }

  screen(fonts: ReadonlyMap<FontId, FontAtlas>): ShellScreen {
    const font = atlas(fonts, "FONT3");
    const heading = atlas(fonts, "FONT1");
    if (this.model.view === "realism") return realismScreen({ panel: this.model.realism });
    if (this.model.view === "bestMissions") {
      // Ranked and capped here, not trusted from storage as-is — `recordBestMission`
      // always writes a sorted, capped table, but the screen should not depend on that.
      const entries = this.bestMissionsStore ? bestMissions(loadBestMissions(this.bestMissionsStore)) : [];
      return bestMissionsScreen(entries, heading);
    }
    const spec = this.spec();
    const slot = this.model.slot ? gridSlotById(this.model.slot) : undefined;
    // A straight-to-briefing slot names its own catalog id; the historic picker
    // slot's target is just `{ kind: "picker" }` — the id it flies comes from
    // whichever encounter the player picked (`model.historic`), and the panel
    // takes the encounter's own name rather than the grid button's, since
    // "FLY A HISTORIC MISSION" names the picker, not the sortie.
    const catalogId =
      slot?.target.kind === "mission" ? slot.target.catalogId : slot?.target.kind === "picker" ? this.model.historic : undefined;
    if (!spec || !slot || !catalogId) return missionGridScreen(this.model);
    const title = slot.target.kind === "picker" ? spec.title : slot.label;
    if (this.model.view === "briefing") {
      return missionBriefingScreen(this.model, spec, title, catalogId, font, heading);
    }
    if (this.model.view === "assignment") return missionAssignmentScreen(spec, font, heading, ASSIGNMENT_ACTIONS);
    if (this.model.view === "roster") return flightAssignmentsScreen(spec, font, heading, ROSTER_ACTIONS);
    return missionGridScreen(this.model);
  }

  dialog(): ShellDialog | undefined {
    return this.model.dialog;
  }

  focus(): number {
    return this.model.focus;
  }

  setFocus(index: number): void {
    this.model = { ...this.model, focus: index };
  }

  setDialog(dialog: ShellDialog | undefined): void {
    this.model = { ...this.model, dialog };
  }

  typeKey(): boolean {
    return false;
  }

  run(action: MenuAction | undefined): string | undefined {
    if (!action) return undefined;
    if (action.kind === "navigate") return action.path;
    if (action.kind === "close") {
      this.model = { ...this.model, dialog: undefined };
      return undefined;
    }
    if (action.kind !== "command") return undefined;
    const panel = applyRealismCommand(this.model.realism, action.command, this.opening);
    if (panel) {
      // Closes the `START POSITION` dialog too — it is a `ShellDialog`, not the
      // Realism Panel screen, so nothing else clears it once a row is picked.
      this.model = { ...this.model, realism: panel, dialog: undefined };
      if (this.store) saveRealism(this.store, panel);
      return undefined;
    }
    // `open:<field>` needs the spec currently on screen (era, nation) to know
    // which aeroplanes/regions/loads are on offer — the model itself is spec-blind.
    const field = paramFieldOf(action.command);
    const spec = field ? this.spec() : undefined;
    if (field && spec) {
      this.model = { ...this.model, dialog: parameterDialog(field, spec) };
      return undefined;
    }
    const next = missionsCommand(this.model, action.command);
    this.model = next.model;
    return next.effect?.kind === "navigate" ? next.effect.path : undefined;
  }
}

function atlas(fonts: ReadonlyMap<FontId, FontAtlas>, id: FontId): FontAtlas {
  const found = fonts.get(id);
  if (!found) throw new Error(`shell: font atlas ${id} was never loaded`);
  return found;
}

export function createMissionsSurface(store: SettingsStore | undefined): MissionsSurface {
  return new MissionsSurface(store);
}

export type { MissionsSurface };
